import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSessionStore } from '@/features/sessions/store/sessionStore'
import { useUpdateCourse, useCourse } from '@/features/courses/hooks/useCourses'
import {
  useSessionMessages,
  useCourseDocuments,
  useSession,
} from '@/features/sessions/hooks/useSessions'
import { useSSEStream } from '@/features/sessions/hooks/useSSEStream'
import { ChatContainer } from '@/features/sessions/components/ChatContainer'
import { ChatInput } from '@/features/sessions/components/ChatInput'
import { RAGIndicator } from '@/features/sessions/components/RAGIndicator'
import { DocumentsSidebar } from '@/features/sessions/components/DocumentsSidebar'
import { SessionResultScreen } from '@/features/sessions/components/SessionResultScreen'
import { Skeleton } from '@/components/ui/Skeleton'
import type { SessionMessage } from '@/features/sessions/types'

/**
 * Aperçu d'une conversation en cours de chargement : deux bulles et la zone de
 * saisie, à la place et à la taille qu'elles auront.
 */
function SessionSkeleton() {
  const { t } = useTranslation()

  return (
    <div
      role="status"
      aria-label={t('session.loading')}
      className="flex h-full flex-col justify-between gap-4 p-4"
    >
      <div className="flex flex-col gap-4">
        <Skeleton className="h-16 w-3/4" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-16 w-2/3 self-end" />
      </div>
      <Skeleton className="h-14 w-full rounded-full" />
    </div>
  )
}

export function SessionPage() {
  const { t } = useTranslation()
  const { courseId = '', sessionId = '' } = useParams<{
    courseId: string
    sessionId: string
  }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const {
    currentSession,
    messages,
    streamingMessage,
    pendingFirstMessage,
    setMessages,
    addMessage,
    setPendingFirstMessage,
    setCurrentSession,
  } = useSessionStore()

  // Fetch session from API when navigating directly (store is empty)
  const { data: fetchedSession, isLoading: sessionLoading, isError: sessionError } =
    useSession(currentSession ? '' : sessionId)

  // Populate store when session is fetched via API (direct URL navigation)
  useEffect(() => {
    if (fetchedSession && !currentSession) {
      setCurrentSession(fetchedSession)
    }
  }, [fetchedSession, currentSession, setCurrentSession])

  const { data: course } = useCourse(courseId)
  const { data: initialMessages, isLoading: messagesLoading } =
    useSessionMessages(sessionId)

  const courseMode = course?.mode ?? 'free'
  const { data: courseDocs } = useCourseDocuments(courseId, courseMode !== 'free')
  const readyDocumentsCount = courseDocs?.filter((d) => d.status === 'ready').length ?? 0

  const updateCourse = useUpdateCourse()
  const { isStreaming, startStream, stopStream } = useSSEStream(sessionId || null)

  // Cancel an in-progress stream and discard the partial AI message.
  const { setStreamingMessage } = useSessionStore()
  const handleStop = useCallback(() => {
    stopStream()
    setStreamingMessage(null)
  }, [stopStream, setStreamingMessage])

  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const titleInputRef = useRef<HTMLInputElement>(null)

  // Sync query messages into store on initial load
  useEffect(() => {
    if (initialMessages) {
      setMessages(initialMessages)
    }
  }, [initialMessages, setMessages])

  // Invalidate messages once streaming ends
  const prevIsStreamingRef = useRef(false)
  useEffect(() => {
    if (prevIsStreamingRef.current && !isStreaming && sessionId) {
      void qc.invalidateQueries({ queryKey: ['sessions', sessionId, 'messages'] })
    }
    prevIsStreamingRef.current = isStreaming
  }, [isStreaming, qc, sessionId])

  // Auto-send pending first message once session is active
  const pendingRef = useRef<string | null>(null)
  useEffect(() => {
    if (pendingFirstMessage && !pendingRef.current) {
      pendingRef.current = pendingFirstMessage
      setPendingFirstMessage(null)
    }
  }, [pendingFirstMessage, setPendingFirstMessage])

  function handleTitleClick() {
    if (course) {
      setTitleDraft(course.title)
      setEditingTitle(true)
      setTimeout(() => titleInputRef.current?.select(), 0)
    }
  }

  function handleTitleSave() {
    const trimmed = titleDraft.trim()
    if (trimmed && trimmed !== course?.title && courseId) {
      updateCourse.mutate({ id: courseId, data: { title: trimmed } })
    }
    setEditingTitle(false)
  }

  function handleTitleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') handleTitleSave()
    if (e.key === 'Escape') setEditingTitle(false)
  }

  const sessionStatus = currentSession?.status

  const handleSend = useCallback(
    (content: string) => {
      if (!sessionId || sessionStatus !== 'active' || isStreaming) return

      const messageUuid = crypto.randomUUID()

      const userMsg: SessionMessage = {
        id: `optimistic-${messageUuid}`,
        sessionId,
        role: 'user',
        content,
        messageUuid,
        tokensUsed: 0,
        metadata: null,
        createdAt: new Date().toISOString(),
      }

      addMessage(userMsg)
      startStream(content, messageUuid)
    },
    [sessionId, sessionStatus, isStreaming, addMessage, startStream],
  )

  // Le premier message saisi sur /chat est rejoué dès que la session est prête.
  useEffect(() => {
    const pending = pendingRef.current
    if (!pending || sessionStatus !== 'active' || isStreaming || messages.length > 0) return

    pendingRef.current = null
    handleSend(pending)
  }, [sessionStatus, isStreaming, messages.length, handleSend])

  // Loading state while fetching session from API (direct URL navigation)
  if (!currentSession && sessionLoading) {
    return <SessionSkeleton />
  }

  // Session not found or unauthorized
  if (!currentSession && sessionError) {
    return (
      <div className="flex h-full items-center justify-center bg-surface">
        <div className="text-center px-8">
          <p className="text-sm text-gray-500 mb-4">
            {t('session.not_found')}
          </p>
          <button
            type="button"
            onClick={() => navigate('/chat')}
            className="px-4 py-2.5 text-sm font-medium bg-primary text-white rounded-full hover:opacity-90 transition-opacity"
          >
            {t('session.new_conversation')}
          </button>
        </div>
      </div>
    )
  }

  // Still waiting for fetchedSession to populate the store
  if (!currentSession) {
    return <SessionSkeleton />
  }

  // Only fully disable the input when the session itself is not active.
  // Streaming is handled separately via the Stop button.
  const isInputDisabled = currentSession.status !== 'active'

  let chatPlaceholder = t('session.placeholder')
  if (currentSession.status === 'paused') {
    chatPlaceholder = t('session.paused_placeholder')
  } else if (isStreaming) {
    chatPlaceholder = t('session.streaming_placeholder')
  }

  if (currentSession.status === 'ended') {
    return <SessionResultScreen session={currentSession} courseId={courseId} />
  }

  return (
    <div className="flex flex-col h-full bg-surface">
      {/* Session title bar — minimal, below the ChatLayout top bar */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-outline-variant/30 shrink-0 bg-surface">
        {/* Editable title */}
        {editingTitle ? (
          <input
            ref={titleInputRef}
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={handleTitleSave}
            onKeyDown={handleTitleKeyDown}
            className="flex-1 text-sm font-semibold text-on-surface bg-surface-low border border-primary rounded-lg px-2 py-1 focus:outline-none"
            maxLength={100}
          />
        ) : (
          <button
            type="button"
            onClick={handleTitleClick}
            className="flex-1 text-sm font-medium text-on-surface-variant truncate text-left hover:text-on-surface transition-colors"
            title={t('session.rename_hint')}
          >
            {course?.title ?? t('session.default_title')}
          </button>
        )}
      </div>

      {/* RAG mode indicator */}
      <RAGIndicator mode={courseMode} readyDocumentsCount={readyDocumentsCount} />

      {/* Chat + optional documents sidebar */}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex flex-col flex-1 min-w-0">
          {/* Chat area */}
          <ChatContainer
            messages={messages}
            streamingMessage={streamingMessage}
            isLoading={messagesLoading}
            onSendMessage={handleSend}
          />

          {/* Une session terminée est déjà rendue plus haut : ici elle est active ou en pause. */}
          <ChatInput
            onSend={handleSend}
            onStop={handleStop}
            disabled={isInputDisabled}
            isStreaming={isStreaming}
            placeholder={chatPlaceholder}
          />
        </div>

        {/* Documents sidebar (RAG / hybrid only) */}
        <DocumentsSidebar courseId={courseId} mode={courseMode} />
      </div>
    </div>
  )
}
